"use client"

import { router, usePage } from "@inertiajs/react"
import { Plus } from "@untitledui/icons"
import { useReducedMotion } from "motion/react"
import { useCallback, useEffect, useRef, useState } from "react"

import { Button } from "@/components/ui/button"
import { DestructiveStackedLeftAlignedModal } from "@/components/ui/destructive-stacked-left-aligned-modal"
import {
  adminCollabTrackPromptPath,
  adminCollabTrackPromptsPath,
  reorderAdminCollabTrackPromptsPath,
  restoreAdminCollabTrackPromptPath,
} from "@/routes"
import type { TrackPromptSummary } from "@/types/contents"

import type { PromptFormValues } from "./prompt-form-modal"
import { PromptFormModal } from "./prompt-form-modal"
import { PromptListsItemUpDownButton } from "./prompt-lists-item-up-down-button"

const EMPTY_FORM: PromptFormValues = {
  title: "",
  guidance: "",
  content_type: "audio",
  curation_state: "pending",
}

const sortByPosition = (prompts: TrackPromptSummary[]) =>
  [...prompts].sort((a, b) => a.position - b.position)

// Prompts beyond this count start collapsed behind a "See more" CTA.
const MAX_CLIP = 6
const REQUEST_DEBOUNCE = 200

/**
 * PromptDisplayAsLists renders a collab's prompts as a reorderable list,
 * with add/edit/delete and up-down-button reordering.
 * @param collabSlug - Collab slug used to build the track-prompt API routes.
 * @param trackPrompts - Prompts to render.
 * @returns The prompts list, its add/edit modal, and its delete modal.
 */
export function PromptDisplayAsLists({
  collabSlug,
  trackPrompts,
}: {
  collabSlug: string
  trackPrompts: TrackPromptSummary[]
}) {
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const { errors: pageErrors } = usePage<{
    errors?: Record<string, string[]>
  }>().props

  const [modalMode, setModalMode] = useState<"add" | "edit" | null>(null)
  const [activePrompt, setActivePrompt] = useState<TrackPromptSummary | null>(
    null,
  )
  const [hasSubmitted, setHasSubmitted] = useState(false)
  const [processing, setProcessing] = useState(false)
  const [promptToDelete, setPromptToDelete] =
    useState<TrackPromptSummary | null>(null)
  const [expanded, setExpanded] = useState(false)
  const reduceMotion = useReducedMotion()

  // `order` is the optimistic reorder source of truth; it reconciles with
  // the server `trackPrompts` prop whenever that prop changes (the "adjust
  // state from props" pattern — see ClaimTrackBoard's `order`).
  // Reorders now land in a single request, so there's no multi-step window to guard.
  const [order, setOrder] = useState(() => sortByPosition(trackPrompts))
  const [prevTrackPrompts, setPrevTrackPrompts] = useState(trackPrompts)
  if (trackPrompts !== prevTrackPrompts) {
    setPrevTrackPrompts(trackPrompts)
    setOrder(sortByPosition(trackPrompts))
  }
  const sortedPrompts = order
  // While collapsed, only the clipped prefix renders — Reorder.Group's
  // `values` must match what's on screen.
  const visiblePrompts =
    expanded || sortedPrompts.length <= MAX_CLIP
      ? sortedPrompts
      : sortedPrompts.slice(0, MAX_CLIP)
  const clippedCount = sortedPrompts.length - MAX_CLIP

  // Errors land as a flashed `errors` page prop after the controller's
  // redirect, not in useForm().errors — only show them once this modal's
  // own submit has resolved, to avoid flashing a stale or unrelated error.
  const formErrors = hasSubmitted && !processing ? pageErrors : undefined

  const openAddModal = () => {
    setActivePrompt(null)
    setModalMode("add")
    setHasSubmitted(false)
  }

  const openEditModal = (prompt: TrackPromptSummary) => {
    setActivePrompt(prompt)
    setModalMode("edit")
    setHasSubmitted(false)
  }

  const closeModal = () => {
    setModalMode(null)
    setActivePrompt(null)
    setHasSubmitted(false)
  }

  const handleSubmitPrompt = (values: PromptFormValues) => {
    setHasSubmitted(true)
    setProcessing(true)

    const options = {
      preserveScroll: true,
      onSuccess: closeModal,
      onFinish: () => setProcessing(false),
    }

    if (modalMode === "edit" && activePrompt) {
      router.patch(
        adminCollabTrackPromptPath(collabSlug, activePrompt.id),
        { track_prompt: values },
        options,
      )
    } else {
      router.post(
        adminCollabTrackPromptsPath(collabSlug),
        { track_prompt: values },
        options,
      )
    }
  }

  const handleConfirmDelete = () => {
    if (!promptToDelete) return
    router.delete(adminCollabTrackPromptPath(collabSlug, promptToDelete.id), {
      preserveScroll: true,
    })
    setPromptToDelete(null)
  }

  const handleRestore = (prompt: TrackPromptSummary) => {
    router.patch(
      restoreAdminCollabTrackPromptPath(collabSlug, prompt.id),
      {},
      { preserveScroll: true },
    )
  }

  // Swaps `prompt` with its neighbour in `direction` and persists.
  const handleMove = (prompt: TrackPromptSummary, direction: -1 | 1) => {
    const currentIndex = order.findIndex((p) => p.id === prompt.id)
    const targetIndex = currentIndex + direction
    if (currentIndex === -1 || targetIndex < 0 || targetIndex >= order.length) {
      return
    }

    const next = [...order]
    ;[next[currentIndex], next[targetIndex]] = [
      next[targetIndex],
      next[currentIndex],
    ]
    setOrder(next)
    persistReorder(next)
  }

  // Sends the whole order as one request — the server renumbers every
  // prompt to a contiguous sequence in one transaction.
  // Enhanced caching for needed request variables (collabSlug, trackPrompts).
  // Implemented debounce & re-using processing state for blocking CTA.
  const persistReorder = useCallback(
    (next: TrackPromptSummary[]) => {
      setProcessing(true)

      if (debounceTimer.current) clearTimeout(debounceTimer.current)

      debounceTimer.current = setTimeout(() => {
        router.patch(
          reorderAdminCollabTrackPromptsPath(collabSlug),
          { ids: next.map((p) => p.id) },
          {
            preserveScroll: true,
            onError: () => {
              setProcessing(false)
              setOrder(sortByPosition(trackPrompts))
            },
            onFinish: () => {
              setProcessing(false)
            },
          },
        )
      }, REQUEST_DEBOUNCE)
    },
    [collabSlug, trackPrompts],
  )

  // Clear any pending timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current)
    }
  }, [])

  return (
    <div aria-label="Prompts" className="flex flex-col gap-3">
      <div className="flex items-center justify-end">
        <Button
          size="sm"
          color="link-color"
          iconLeading={Plus}
          onClick={openAddModal}
          className="*:data-icon:text-brand-secondary hover:*:data-icon:text-brand-secondary_hover"
        >
          Add prompt
        </Button>
      </div>

      {sortedPrompts.length === 0 ? (
        <p className="text-tertiary py-6 text-center text-sm">
          No prompts yet.
        </p>
      ) : (
        <>
          <div className="flex flex-col gap-2">
            {visiblePrompts.map((prompt, index) => (
              <PromptListsItemUpDownButton
                key={prompt.id}
                prompt={prompt}
                isFirst={index === 0}
                isLast={
                  index === visiblePrompts.length - 1 &&
                  visiblePrompts.length === sortedPrompts.length
                }
                isDisabled={processing}
                animateLayout={!reduceMotion}
                onMoveUp={() => handleMove(prompt, -1)}
                onMoveDown={() => handleMove(prompt, 1)}
                onEdit={() => openEditModal(prompt)}
                onRemove={() => setPromptToDelete(prompt)}
                onRestore={() => handleRestore(prompt)}
              />
            ))}
          </div>

          {clippedCount > 0 && (
            <div className="flex justify-center">
              <Button
                type="button"
                size="sm"
                color="link-color"
                onClick={() => setExpanded((prev) => !prev)}
              >
                {expanded ? "See less" : `See more (${clippedCount})`}
              </Button>
            </div>
          )}
        </>
      )}

      {modalMode && (
        <PromptFormModal
          isOpen
          onOpenChange={(open) => !open && closeModal()}
          mode={modalMode}
          initialValues={
            activePrompt
              ? {
                  title: activePrompt.title,
                  guidance: activePrompt.guidance ?? "",
                  content_type: activePrompt.content_type,
                  curation_state: activePrompt.curation_state,
                }
              : EMPTY_FORM
          }
          errors={formErrors}
          processing={processing}
          onSubmit={handleSubmitPrompt}
        />
      )}

      <DestructiveStackedLeftAlignedModal
        isOpen={!!promptToDelete}
        onOpenChange={(open) => !open && setPromptToDelete(null)}
        title={`Remove "${promptToDelete?.title ?? ""}"?`}
        description={
          promptToDelete?.suggestions_count
            ? `This prompt holds ${promptToDelete.suggestions_count} suggestion${
                promptToDelete.suggestions_count === 1 ? "" : "s"
              } from the community. Removing it hides the slot and its suggestions from the Collab. You can restore it later.`
            : "Removing this prompt hides the slot from the Collab. You can restore it later."
        }
        onConfirm={handleConfirmDelete}
        confirmLabel="Remove"
      />
    </div>
  )
}
